<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import type {
        InteractiveTitleSettingDefinition,
        InteractiveTitleInputDefinition,
    } from '../types';
    import InteractiveTitleInput from './InteractiveTitleInput.svelte';
    import { titleParser } from '../utils';
    import { TitleToken } from '../utils';
    import type { TitleAlign } from 'portlet/models/settings/settingDefinitions/titleAlignSetting';

    interface Props {
        title: string;
        titleAlign: TitleAlign;
        replacementTags: Record<string, string>;
        interactiveReplacementTags?: Record<string, InteractiveTitleSettingDefinition>;
    }

    let { title, titleAlign, replacementTags, interactiveReplacementTags = {} }: Props = $props();

    let titleParts = $derived(titleParser(title, replacementTags, interactiveReplacementTags));
    let headerTitle = $derived(TitleToken.toString(titleParts));
    let leftAlign = $derived(titleAlign === 'left');
    let rightAlign = $derived(titleAlign === 'right');
    let centerAlign = $derived(titleAlign === 'center');

    const dispatch = createEventDispatcher();

    function onInteractiveTitleChange(
        definition: InteractiveTitleSettingDefinition,
        input: InteractiveTitleInputDefinition,
    ) {
        dispatch('interactive-title-change', definition.format(input.value));
    }

    function handleDblClick(e: Event) {
        e.stopPropagation();
        dispatch('dblclick', e);
    }
</script>

<h4
    class="header__title"
    title={headerTitle}
    class:title-align-center={centerAlign}
    class:title-align-left={leftAlign}
    class:title-align-right={rightAlign}
>
    {#each titleParts as part}
        {#if part.type === 'replacement' && part.value !== null}
            {part.value}
        {:else if part.type === 'interactive' && part.value !== null}
            <span class="interactiveTitle" ondblclick={handleDblClick}>
                <InteractiveTitleInput
                    field={part.value.field}
                    on:change={event =>
                        part.value && onInteractiveTitleChange(part.value, event.detail)}
                />
            </span>
        {:else}{part.text}{/if}
    {/each}
</h4>

<style lang="scss">
    .interactiveTitle {
        :global(select),
        :global(input) {
            width: auto;
            padding: 4px 8px;
            border-radius: 4px;

            border: none;
            background-color: #e9edf3;
            margin: 0;
        }

        :global(input) {
            text-align: center;

            //disable arrows
            -moz-appearance: textfield;
            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
        }
        :global(select) {
            text-align: left;
            box-sizing: content-box;
            cursor: pointer;
            //disable arrow
            -moz-appearance: none;
            -webkit-appearance: none;
        }
    }
    .header__title {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        text-align: var(--title-align, center);
        font-size: var(--title-font-size, 1em);
        font-weight: var(--title-font-weight);
        color: var(--title-color, var(--color, black));
        margin: var(--title-margin, 0);
        flex-grow: 1;
        &.title-align-center {
            text-align: center;
        }
        &.title-align-left {
            text-align: left;
        }
        &.title-align-right {
            text-align: right;
        }
    }
</style>
